import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { createAvatar } from "@dicebear/core";
import { identicon } from "@dicebear/collection";
import { REACTIONS, REACTION_BY_KEY } from "../utils/reactions";
import { relativeTime } from "../utils/post";

const PAGE_SIZE = 20;

/**
 * Who reacted to a post, opened from the count beside the reaction button.
 *
 * Tabs are built from the counts the page already has, so the modal opens with
 * the right tabs before its own request comes back and nothing reflows when it
 * does. "All" is always first; the rest follow the canonical reaction order
 * rather than count order, so the row of tabs does not reshuffle as people
 * react.
 */
const ReactionsModal = ({ show, onHide, postId, reactions }) => {
  // Memoised because the `|| {}` would otherwise hand back a new object on
  // every render, and the tab-reset effect below depends on it.
  const counts = useMemo(() => reactions || {}, [reactions]);
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const present = REACTIONS.filter((r) => counts[r.key] > 0);

  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(0);
  const [tabTotal, setTabTotal] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | loading | more | error

  const load = useCallback(
    (page, replace) => {
      setStatus(replace ? "loading" : "more");
      const query = new URLSearchParams({ page, size: PAGE_SIZE });
      if (tab !== "all") query.set("reaction", tab);

      fetch(`/api/votes/post/${postId}?${query}`)
        .then((res) => {
          if (!res.ok) throw new Error(`Reactor list failed: ${res.status}`);
          return res.json();
        })
        .then((data) => {
          const withAvatars = data.reactors.map((r) => ({
            ...r,
            // The same seed the feed cards and /topcontributors use, so a
            // reactor's face matches their own posts.
            avatar: createAvatar(identicon, { seed: r.displayname }).toDataUri(),
          }));
          setItems((current) =>
            replace ? withAvatars : [...current, ...withAvatars]
          );
          setLoaded((current) =>
            replace ? withAvatars.length : current + withAvatars.length
          );
          setTabTotal(data.total);
          setStatus("idle");
        })
        .catch((error) => {
          console.error("Error:", error);
          setStatus("error");
        });
    },
    [postId, tab]
  );

  // Refetch on open and on every tab change. No cache: the modal unmounts
  // between visits, and a stale list of who reacted is worse than a spinner.
  useEffect(() => {
    if (!show) return;
    setItems([]);
    setLoaded(0);
    load(1, true);
  }, [show, tab, load]);

  // A tab can disappear while the modal is open — the viewer takes back the
  // only 😮 — which would otherwise leave it on a tab that no longer exists.
  useEffect(() => {
    if (show && tab !== "all" && !counts[tab]) setTab("all");
  }, [show, tab, counts]);

  const handleHide = () => {
    setTab("all");
    onHide();
  };

  const remaining = tabTotal - loaded;

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      scrollable
      contentClassName="bleep-light bl-page--dark post-modal bl-reactors"
    >
      {/* Bootstrap's close glyph is near-black, which disappears on the dark
          modal surface. */}
      <Modal.Header closeVariant="white" closeButton>
        <Modal.Title className="post-modal__title">
          <span className="post-modal__titles">
            <span className="post-modal__heading">Reactions</span>
            <span className="post-modal__sub">
              {total} {total === 1 ? "person" : "people"} reacted to this
            </span>
          </span>
        </Modal.Title>
      </Modal.Header>

      {/* Outside the scrollable body: the tabs are how you move around this
          list, so they must not scroll away from it. */}
      {present.length > 1 && (
        <div className="bl-reactors__tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "all"}
            className={`bl-reactors__tab${tab === "all" ? " is-active" : ""}`}
            onClick={() => setTab("all")}
          >
            All <span className="bl-reactors__tab-count">{total}</span>
          </button>
          {present.map((r) => (
            <button
              key={r.key}
              type="button"
              role="tab"
              aria-selected={tab === r.key}
              aria-label={`${r.label}, ${counts[r.key]}`}
              className={`bl-reactors__tab${tab === r.key ? " is-active" : ""}`}
              onClick={() => setTab(r.key)}
            >
              <span aria-hidden="true">{r.emoji}</span>
              <span className="bl-reactors__tab-count" aria-hidden="true">
                {counts[r.key]}
              </span>
            </button>
          ))}
        </div>
      )}

      <Modal.Body className="bl-reactors__body">
        {status === "loading" && (
          <ul className="bl-reactors__list" aria-hidden="true">
            {Array.from({ length: Math.min(total || 3, 6) }).map((_, i) => (
              <li className="bl-reactors__row bl-pulse" key={i}>
                <span className="bl-reactors__avatar bl-reactors__avatar--ghost" />
                <span className="bl-reactors__ghost-line" />
              </li>
            ))}
          </ul>
        )}

        {status === "error" && items.length === 0 && (
          <p className="bl-reactors__note">
            That list could not be loaded just now.
          </p>
        )}

        {status !== "loading" && items.length > 0 && (
          <>
            <ul className="bl-reactors__list">
              {items.map((r) => {
                const def = REACTION_BY_KEY[r.reaction];
                return (
                  <li className="bl-reactors__row" key={`${r.id}-${r.reaction}`}>
                    {/* Disc + inset image, not a bare <img>: an identicon is
                        dark shapes on a transparent SVG and needs the light
                        backing every other avatar on the site draws. */}
                    <span className="bl-reactors__avatar">
                      <img src={r.avatar} alt="" aria-hidden="true" />
                    </span>
                    <span className="bl-reactors__who">
                      <span className="bl-reactors__name">{r.displayname}</span>
                      <span className="bl-reactors__when">
                        {relativeTime(r.dateTime)}
                      </span>
                    </span>
                    <span
                      className="bl-reactors__emoji"
                      title={def ? def.label : undefined}
                    >
                      <span aria-hidden="true">{def ? def.emoji : "👍"}</span>
                      <span className="bl-sr-only">
                        reacted {def ? def.label : r.reaction}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>

            {remaining > 0 && (
              <button
                type="button"
                className="bl-reactors__more"
                onClick={() => load(Math.floor(loaded / PAGE_SIZE) + 1, false)}
                disabled={status === "more"}
              >
                {status === "more"
                  ? "Loading…"
                  : `Show ${Math.min(remaining, PAGE_SIZE)} more`}
              </button>
            )}
          </>
        )}

        {status === "idle" && items.length === 0 && (
          <p className="bl-reactors__note">Nobody has reacted yet.</p>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ReactionsModal;
